import React, { useState, useRef, useEffect } from 'react';
import { X, PlusCircle } from 'lucide-react';
import * as S from '../AdminDashboard.style';

const AddQuestionModal = ({ onClose, onSubmit }) => {
  const [currentPath, setCurrentPath] = useState([]);
  const [questionsData, setQuestionsData] = useState([]);
  const [questionType, setQuestionType] = useState('text');
  const [question, setQuestion] = useState('');
  const [mcqOptions, setMcqOptions] = useState(['']);
  const [isFirstQuestion, setIsFirstQuestion] = useState(true);
  const [showSubQuestionButton, setShowSubQuestionButton] = useState(false);
  const nextIdRef = useRef(1);

  useEffect(() => {
    const isValid =
      question.trim() !== '' &&
      (questionType === 'text' ||
        (questionType === 'mcq' && mcqOptions.filter((opt) => opt.trim() !== '').length >= 1));
    setShowSubQuestionButton(isValid);
  }, [question, mcqOptions, questionType]);

  const handleAddOption = () => {
    setMcqOptions((prevOptions) => [...prevOptions, '']);
  };

  const handleOptionChange = (index, value) => {
    setMcqOptions((prevOptions) => {
      const newOptions = [...prevOptions];
      newOptions[index] = value;
      return newOptions;
    });
  };

  const resetForm = () => {
    setQuestion('');
    setMcqOptions(['']);
    setQuestionType('text');
  };

  const createQuestionObject = () => {
    const baseQuestion = {
      id: nextIdRef.current,
      type: questionType,
      question: question,
      subQuestions: [],
    };
    if (questionType === 'mcq') {
      baseQuestion.options = mcqOptions.filter((opt) => opt.trim() !== '');
    }
    nextIdRef.current += 1;
    return baseQuestion;
  };

  const addQuestionToPath = () => {
    const newQuestion = createQuestionObject();

    setQuestionsData((prevData) => {
      const updatedData = [...prevData];

      const updateNestedQuestions = (questions, path, index = 0) => {
        const currentIndex = path[index];
        if (index === path.length - 1) {
          if (!questions[currentIndex]) {
            return questions;
          }
          questions[currentIndex].subQuestions = questions[currentIndex].subQuestions || [];
          questions[currentIndex].subQuestions.push(newQuestion);
          return questions;
        }

        questions[currentIndex].subQuestions = questions[currentIndex].subQuestions || [];
        questions[currentIndex].subQuestions = updateNestedQuestions(
          questions[currentIndex].subQuestions,
          path,
          index + 1
        );
        return questions;
      };

      if (currentPath.length === 0) {
        if (isFirstQuestion) {
          return [newQuestion];
        } else {
          updatedData.push(newQuestion);
        }
      } else {
        updateNestedQuestions(updatedData, currentPath);
      }
      return updatedData;
    });

    setIsFirstQuestion(false);
    resetForm();
  };

  const handleAddSubQuestion = () => {
    addQuestionToPath();

    setQuestionsData((prevData) => {
      const parentQuestions = getCurrentSubQuestions(prevData);
      const newIndex = parentQuestions.length - 1;
      setCurrentPath((prevPath) => [...prevPath, newIndex]);
      return prevData;
    });
  };

  // const handleGoBack = () => {
  //   if (currentPath.length > 0) {
  //     setCurrentPath((prevPath) => prevPath.slice(0, -1));
  //   }
  // };

  const getCurrentSubQuestions = (data = questionsData) => {
    let current = data;
    for (const index of currentPath) {
      if (!current[index]) return [];
      current = current[index].subQuestions || [];
    }
    return current;
  };

  const isFormValid = () => {
    return (
      question.trim() !== '' &&
      (questionType === 'text' ||
        (questionType === 'mcq' && mcqOptions.filter((opt) => opt.trim() !== '').length >= 1))
    );
  };

  const handleFinish = () => {
    if (isFormValid()) {
      addQuestionToPath();
      onSubmit(questionsData);
      resetForm();
      setQuestionsData([]);
      setCurrentPath([]);
      setIsFirstQuestion(true);
      nextIdRef.current = 1;
      onClose();
    }
  };

  const getCurrentPathDisplay = () => {
    if (currentPath.length === 0) return 'Main Question';
    return `Sub-Question Level ${currentPath.length}`;
  };

  return (
    <S.Modal>
      <S.ModalContent>
        <S.ModalHeader>
          <h3>Add New Question - {getCurrentPathDisplay()}</h3>
          {currentPath.length > 0 && (
            <S.BreadcrumbPath>
              {currentPath.map((_, index) => (
                <span key={index}>➔ Level {index + 1}</span>
              ))}
            </S.BreadcrumbPath>
          )}
        </S.ModalHeader>

        <S.FormField>
          <S.Label>Question Type</S.Label>
          <S.Select value={questionType} onChange={(e) => setQuestionType(e.target.value)}>
            <option value="text">Text</option>
            <option value="mcq">Multiple Choice</option>
          </S.Select>
        </S.FormField>

        <S.FormField>
          <S.Label>Question</S.Label>
          <S.Input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Enter your question"
          />
        </S.FormField>

        {questionType === 'mcq' && (
          <S.FormField>
            <S.Label>Options</S.Label>
            {mcqOptions.map((option, index) => (
              <S.Input
                key={index}
                type="text"
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
              />
            ))}
            <S.AddOptionButton onClick={handleAddOption}>
              <PlusCircle size={16} />
              Add Option
            </S.AddOptionButton>
          </S.FormField>
        )}

        <S.ModalFooter>
          <S.AddSubQuestionButton onClick={onClose}>
            <X size={16} />
            Close
          </S.AddSubQuestionButton>
          <S.ButtonGroup>
            {showSubQuestionButton && (
              <S.AddSubQuestionButton onClick={handleAddSubQuestion}>
                <PlusCircle size={16} />
                Add Sub-Question
              </S.AddSubQuestionButton>
            )}
            {isFormValid() && <S.FinishButton onClick={handleFinish}>Finish</S.FinishButton>}
          </S.ButtonGroup>
        </S.ModalFooter>
      </S.ModalContent>
    </S.Modal>
  );
};

export default AddQuestionModal;
